import { type Dispatch } from "@reduxjs/toolkit";
import {
  fetchBegin as licensesFetchBegin,
  fetchSuccess as licensesFetchSuccess,
  fetchFailure as licensesFetchFailure,
  reset as licensesReset,
  setIsPurchaseNeeded,
} from "../slices/licenseConfirmationSlice";
import {
  fetchBegin as packsFetchBegin,
  fetchSuccess as packsFetchSuccess,
  fetchFailure as packsFetchFailure,
  setContentPack,
  reset as packsReset,
  type FlowAssignment,
} from "../slices/packsContextSlice";
import dataService from "../../../../Application/services/dataServices/typedDataService";
import {
  type LicenseConfirmationData,
  type PacksContextItem,
  type PacksContextItemModel,
} from "../../../../../interfaces";
import AssetTypes from "../../../../../enums/assetTypes";
import EventTypes from "../../../../../enums/eventTypes";
import CommunicationTypes from "../../../../../enums/communicationTypes";
import ItemTypes from "../../../../../enums/packItemsTypes";
import arrayUtils from "../../../../../utils/arrayUtils";
import { Context } from "../../../../../enums/licenseContext";
import {
  type FlowConflict,
  type PackCombination,
  type PacksContextResponseModel,
} from "../../../../../interfaces/licenseConfirmation";
import { type AxiosResponse } from "axios";
import { PotentialFlowConflictCollection } from "./potentialFlowConflictCollection";
import ThreatDefenceContentTypes from "enums/threatDefenceContentTypes";
import { needsLicensePurchase } from "../../LicenseConfirmationModalContent/LicenseConfirmationBlockV2/utils";

const isFlowConflict = (items: PacksContextResponseModel[] | FlowConflict[]): items is FlowConflict[] => {
  return items.length > 0 && items.every((item) => "packCombinations" in item);
};

const fetchUserContentAssignmentInfo = async (
  context: Context,
  options: LicenseConfirmationData,
  packIds: number[],
  additionalPurchaseEnabled: boolean,
) => {
  if (options.packId) {
    return dataService.packs.getPackPeopleAssignmentInfoAsync(packIds, options.packId);
  }

  if (options.flowId) {
    return additionalPurchaseEnabled
      ? dataService.packs.getPackUserFlowAssignmentInfoV2Async(packIds, options.flowId)
      : dataService.packs.getPackUserFlowAssignmentInfoAsync(packIds, options.flowId);
  }

  if (options.count) {
    return dataService.packs.getUserLicenseInfoByGroupMembers(packIds, options.count);
  }

  const shouldIncludeGroupIds = [
    Context.AddPeopleToContent,
    Context.AddPacksToUserOrGroup,
    Context.AddPackToUsersOrGroups,
  ].includes(context);

  const groupIds = shouldIncludeGroupIds ? options.groupIds : [];

  return additionalPurchaseEnabled
    ? dataService.packs.getPackUserContentAssignmentInfoV2Async(packIds, options.userIds, groupIds)
    : dataService.packs.getPackUserContentAssignmentInfoAsync(packIds, options.userIds, groupIds);
};

export const fetchAssignmentInfo =
  (context: Context, options: LicenseConfirmationData, additionalPurchaseEnabled: boolean) =>
  async (dispatch: Dispatch) => {
    dispatch(licensesFetchBegin());

    try {
      let packIds: number[] | undefined = options.packIds;

      if (context === Context.AddPeopleToGroups) {
        const groupPacks = await dataService.packs.getGroupsPacksAsync(options.groupIds!);
        packIds = groupPacks.data.flatMap((gp) => gp.packs.map((p) => p.id));
      }

      if (!packIds || packIds.length === 0) {
        dispatch(licensesFetchSuccess({ items: [], totalCount: 0 }));
        return;
      }

      const userContentAssignmentInfo = await fetchUserContentAssignmentInfo(
        context,
        options,
        packIds,
        additionalPurchaseEnabled,
      );
      const isPurchaseNeeded = additionalPurchaseEnabled && userContentAssignmentInfo.data.some(needsLicensePurchase);

      additionalPurchaseEnabled && dispatch(setIsPurchaseNeeded(isPurchaseNeeded));
      dispatch(
        licensesFetchSuccess({
          items: userContentAssignmentInfo.data,
          totalCount: userContentAssignmentInfo.data.length,
        }),
      );
    } catch (error) {
      dispatch(licensesFetchFailure(error as Error));
    }
  };

export const licensesResetAction = () => async (dispatch: Dispatch) => {
  dispatch(licensesReset());
};

export const setContentPackAction =
  (itemId: string, itemType: string, packId?: number) => async (dispatch: Dispatch) => {
    dispatch(setContentPack({ itemId, itemType, packId }));
  };

export const resetContentPacksAction = () => async (dispatch: Dispatch) => {
  dispatch(packsReset());
};

export const fetchContentPacksAction = (items: PacksContextItem[]) => async (dispatch: Dispatch) => {
  dispatch(packsFetchBegin());
  try {
    const result: PacksContextItemModel[] = [];
    let flowConflicts: Record<number, PackCombination[]> = {};
    let flowAssignments: FlowAssignment[] = [];
    const itemTypesMeta = {
      [AssetTypes.Video.toLowerCase() as Lowercase<AssetTypes.Video>]: {
        fetchData: dataService.assets.getPublishedVideosByIds,
        newItemsIds: [],
      },
      [`${AssetTypes.Assessment.toLowerCase()}`]: {
        fetchData: dataService.assessments.getPublishedAssessmentsByIdsV2,
        newItemsIds: [],
      },
      [`${AssetTypes.Survey.toLowerCase()}`]: {
        fetchData: dataService.surveys.getPublishedSurveysByIds,
        newItemsIds: [],
      },
      [`${EventTypes.ExternalEvent.toLowerCase()}`]: {
        fetchData: dataService.events.getPublishedEventsByIdsAsync,
        newItemsIds: [],
      },
      [`${CommunicationTypes.Email.toLowerCase()}`]: {
        fetchData: dataService.emails.getPublishedEmailsByIdsAsync,
        newItemsIds: [],
      },
      [`${CommunicationTypes.Message.toLowerCase()}`]: {
        fetchData: dataService.messages.getPublishedMessagesByIds,
        newItemsIds: [],
      },
      [`${ItemTypes.Flow.toLowerCase()}`]: {
        fetchData: dataService.flows.getPublishedFlowsByIdsForItemsToDropDesignerAsync,
        newItemsIds: [],
      },
      [`${ThreatDefenceContentTypes.Campaign.toLowerCase()}`]: {
        fetchData: dataService.threatDefence.campaigns.getPublishedCampaignsByIdsAsync,
        newItemsIds: [],
      },
      [`${AssetTypes.Pdf.toLowerCase()}`]: {
        fetchData: dataService.pdfs.getPublishedPdfsByIdsAsync,
        newItemsIds: [],
      },
      pack: {
        newItemsIds: [],
      },
    };

    const packsResults = await getContentPacks(itemTypesMeta, items);

    for (const packResult of packsResults) {
      if (isFlowConflict(packResult.data)) {
        const conflicts = (packsResults as AxiosResponse<FlowConflict[]>[])[0].data;
        const [realConflicts, assignments] = new PotentialFlowConflictCollection(conflicts).toRealConflicts();

        items.forEach((item) => {
          if (!conflicts.some((conflict) => conflict.id === parseInt(item.id))) {
            assignments.push({ flowId: parseInt(item.id), packIds: [] });
          }
        });

        flowAssignments = assignments;
        flowConflicts = realConflicts.reduce((prev, current) => {
          return { ...prev, [current.id]: current.packCombinations };
        }, {});
      } else {
        processContentPacks(packResult.data, itemTypesMeta, items, result);

        const itemDataResults = await getItemsData(itemTypesMeta);
        const allItemsData =
          itemDataResults.length > 0 ? itemDataResults.map((r) => r.data).reduce((a, b) => a.concat(b)) : [];

        result.forEach((item) => {
          const itemData = allItemsData.filter((d: any) => d.id.toString() === item.id)[0];

          if (itemData) {
            item.title = itemData.title;
            item.durationInSeconds = itemData.durationInSeconds;
            item.thumbnailUrl = itemData.thumbnailUrl;
          }
        });
      }
    }

    const packsContext = {
      items: arrayUtils.sortArrayAlphabeticallyByProperty(result, (x) => x.title),
      totalCount: result.length,
      flowConflicts,
      flowAssignments,
    };

    dispatch(packsFetchSuccess(packsContext));
    return packsContext;
  } catch (e) {
    dispatch(packsFetchFailure(e as Error));
  }
};

export const fetchDraftPackContextAction = (items: PacksContextItem[]) => async (dispatch: Dispatch) => {
  dispatch(packsFetchBegin());
  try {
    const item = items[0];
    let flowConflicts: Record<number, PackCombination[]> = {};
    let flowAssignments: FlowAssignment[] = [];

    const packsResults = await dataService.packs.getDraftPackConflictsInfoAsync(parseInt(item.id));

    const conflicts = packsResults.data;
    const [realConflicts, assignments] = new PotentialFlowConflictCollection(conflicts).toRealConflicts();

    if (!conflicts.some((conflict) => conflict.id === parseInt(item.id))) {
      assignments.push({ flowId: parseInt(item.id), packIds: [] });
    }

    flowAssignments = assignments;
    flowConflicts = realConflicts.reduce((prev, current) => {
      return { ...prev, [current.id]: current.packCombinations };
    }, {});

    const packsContext = {
      items: [],
      totalCount: 0,
      flowConflicts,
      flowAssignments,
    };

    dispatch(packsFetchSuccess(packsContext));
    return packsContext;
  } catch (e) {
    dispatch(packsFetchFailure(e as Error));
  }
};

const processContentPacks = (
  contentPacksResult: PacksContextResponseModel[],
  itemTypesMeta: any,
  items: PacksContextItem[],
  result: PacksContextItemModel[],
) => {
  const newItems = contentPacksResult.filter(
    (d) => !items.some((i) => i.id === d.assetId && i.type.toLowerCase() === d.type.toLowerCase()),
  );

  newItems.forEach((newItem) => {
    if (
      newItem.packs.length > 1 &&
      itemTypesMeta[newItem.type.toLowerCase()].newItemsIds.indexOf(newItem.assetId as never) < 0
    ) {
      itemTypesMeta[newItem.type.toLowerCase()].newItemsIds.push(newItem.assetId as never);
    }
  });

  contentPacksResult.forEach((d) => {
    const oldItem = items.filter((i) => i.id === d.assetId && i.type.toLowerCase() === d.type.toLowerCase())[0];
    const existingPackItem = result.filter(
      (r) => r.id === d.assetId && r.type.toLowerCase() === d.type.toLowerCase(),
    )[0];

    if (existingPackItem) {
      existingPackItem.flowIds = existingPackItem.flowIds.concat(d.flowIds ?? []);
    } else {
      result.push({
        id: d.assetId,
        type: d.type,
        packId: d.packs.length === 1 ? d.packs[0].id : undefined,
        packs: arrayUtils.sortArrayAlphabeticallyByProperty(d.packs, (x) => x.name),
        title: oldItem?.title,
        thumbnailUrl: oldItem?.thumbnailUrl,
        durationInSeconds: oldItem?.durationInSeconds,
        flowIds: d.flowIds ?? oldItem?.flowIds ?? [],
      });
    }
  });
};

const getContentPacks = (itemTypesMeta: any, items: PacksContextItem[]) => {
  const filteredContentTypes = Object.keys(itemTypesMeta).filter((type) =>
    items.some((i) => i.type.toLowerCase() === type),
  );

  return Promise.all(
    filteredContentTypes.map((type) => {
      const itemsByType = items.filter((i) => i.type.toLowerCase() === type);
      const ids = itemsByType.map((e) => e.id);

      if (ids.some((id) => !id)) {
        return { data: [] };
      }

      if (type === "pack") {
        return dataService.packs.getPackConflictsInfoAsync(ids);
      }

      if (type === "flow") {
        return dataService.packs.getFlowConflictsInfoAsync(ids);
      }

      return dataService.packs.getContentPacksInfoAsync(type, ids);
    }),
  );
};

const getItemsData = (itemTypesMeta: any) => {
  return Promise.all(
    Object.keys(itemTypesMeta)
      .filter((type) => itemTypesMeta[type].newItemsIds.length > 0)
      .map((type) => {
        return itemTypesMeta[type].fetchData(itemTypesMeta[type].newItemsIds, true);
      }),
  );
};
